import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../ui/cn';
import { ChevronIcon } from './Icons';
import CtaLink from '../ui/CtaLink';

export type MegaGroup = {
  id: string;
  label: string;
  /** Petit titre de rubrique affiché au-dessus du premier groupe qui le porte (ex. le domaine d'un secteur). */
  heading?: string;
  description?: string;
  cta?: { label: string; to: string };
  links: { label: string; to: string }[];
  /** Dernières réalisations du groupe: `undefined` = en cours de chargement, `null` = échec du chargement, `[]` = aucune. */
  projects?: { id: string; title: string; location?: string; to: string }[] | null;
};

/** Panneau déroulant: catégories à gauche, détail et grille de liens à droite. */
export default function MegaMenu({ title, groups, onNavigate, footer, projectsTitle = 'Dernières réalisations', onRetryProjects }: { title: string; groups: MegaGroup[]; onNavigate: () => void; footer?: { label: string; to: string }; projectsTitle?: string; onRetryProjects?: () => void }) {
  const [activeId, setActiveId] = useState(groups[0]?.id);
  const active = groups.find((g) => g.id === activeId) ?? groups[0];
  if (!active) return null;

  return (
    <div className="absolute inset-x-0 top-full animate-fade-in border-t border-line bg-white shadow-[0_20px_40px_-20px_rgba(0,0,0,0.25)]" role="region" aria-label={title}>
      <div className="grid max-h-[calc(100vh-5.5rem)] overflow-y-auto lg:grid-cols-[calc(max(1.5rem,(100vw-72rem)/2)+26rem)_1fr]">
        <div className="bg-white py-10 pl-[max(1.5rem,calc((100vw-72rem)/2))] pr-10">
          <div className="mb-6 text-sm font-semibold uppercase tracking-[0.0625rem] text-ink">{title}</div>
          <ul>
            {groups.map((g, n) => (
              <li key={g.id}>
                {g.heading && g.heading !== groups[n - 1]?.heading && (
                  <div className={cn('pb-1 text-[0.6875rem] font-semibold uppercase tracking-[0.0625rem] text-ink', n > 0 && 'pt-5')}>{g.heading}</div>
                )}
                <button
                  onMouseEnter={() => setActiveId(g.id)}
                  onFocus={() => setActiveId(g.id)}
                  onClick={() => setActiveId(g.id)}
                  className={cn('flex w-full items-center justify-between gap-4 text-left font-display font-semibold leading-tight transition-colors', groups.length > 5 ? 'py-1.5 text-[1.25rem]' : 'py-3 text-[1.625rem]', g.id === active.id ? 'text-charcoal' : 'text-muted hover:text-charcoal')}
                >
                  <span className="relative">
                    {g.label}
                    <span aria-hidden className={cn('absolute -bottom-0.5 left-0 h-0.5 bg-brand transition-all duration-300', g.id === active.id ? 'w-full' : 'w-0')} />
                  </span>
                  <ChevronIcon className="h-2 w-3 shrink-0 -rotate-90" />
                </button>
              </li>
            ))}
          </ul>
          {footer && <div className="mt-6 border-t border-line pt-5" onClick={onNavigate}><CtaLink to={footer.to}>{footer.label}</CtaLink></div>}
        </div>

        <div className="bg-surface py-10 pl-14 pr-[max(1.5rem,calc((100vw-72rem)/2))]">
          <div key={active.id} className="animate-fade-in">
            {/* Le titre est déjà dans la liste de gauche: à droite, seulement le petit détail et le lien */}
            {(active.description || active.cta) && (
              <div className={cn(active.links.length > 0 && 'border-b border-line pb-8')}>
                {active.description && <p className="max-h-[6.5rem] max-w-2xl overflow-hidden text-[1rem] leading-relaxed text-ink [mask-image:linear-gradient(to_bottom,#000_65%,transparent)]">{active.description}</p>}
                {active.cta && <div className="mt-5" onClick={onNavigate}><CtaLink to={active.cta.to}>{active.cta.label}</CtaLink></div>}
              </div>
            )}
            {active.links.length > 0 && (
            <ul className="mt-8 grid gap-x-10 gap-y-1 sm:grid-cols-2 xl:grid-cols-3">
              {active.links.map((l) => (
                <li key={l.to + l.label}>
                  <Link to={l.to} onClick={onNavigate} className="group flex items-center gap-2 py-2.5 text-[1rem] text-black transition-colors hover:text-brand-dark">
                    <span aria-hidden className="h-0.5 w-0 bg-brand transition-all duration-300 group-hover:w-4" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            )}
            {'projects' in active && (
              <div className="mt-8 border-t border-line pt-6">
                <div className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-[0.0625rem] text-ink">{projectsTitle}</div>
                {active.projects === undefined && (
                  <ul aria-busy className="space-y-2">{[0, 1, 2].map((n) => <li key={n} className="h-9 animate-pulse bg-secondary-200" />)}</ul>
                )}
                {active.projects === null && (
                  <p className="text-sm text-ink">
                    Impossible de charger les réalisations.{' '}
                    {onRetryProjects && <button onClick={onRetryProjects} className="font-semibold text-charcoal underline decoration-brand underline-offset-4 hover:text-brand-darker">Réessayer</button>}
                  </p>
                )}
                {active.projects?.length === 0 && <p className="text-sm text-ink">Aucune réalisation à afficher pour le moment.</p>}
                {active.projects && active.projects.length > 0 && (
                  <ul>
                    {active.projects.map((p) => (
                      <li key={p.id}>
                        <Link to={p.to} onClick={onNavigate} className="group flex items-start gap-3 py-2.5">
                          <span aria-hidden className="mt-2.5 h-0.5 w-3 shrink-0 bg-brand transition-all duration-300 group-hover:w-6" />
                          <span className="min-w-0">
                            <span className="block text-[0.9375rem] font-medium leading-snug text-black transition-colors group-hover:text-brand-dark" style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.title}</span>
                            {p.location && <span className="mt-0.5 block text-xs uppercase tracking-[0.0625rem] text-ink">{p.location}</span>}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
